import React from 'react'
import { AmountOverridePayload } from '@/api/Wallet/Types/WithdrawalAutomationDto'
import AddButton from '@/components/AddButton/AddButton'
import {
  FormField,
  FormFieldLayout,
  FormSelectField,
  FormSelectOption,
} from '@/components/Form/FormField'
import { Toggle } from '@/components/Toggle/Toggle'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldArray, Form, FormikProps, withFormik } from 'formik'

import { hasAtLeastOneScope, toScopePayload } from './overrideScope'
import { OverrideScopeFields, ScopeOptions } from './OverrideScopeFields'
import { amountOverrideValidationSchema } from './OverrideValidationSchema'

interface OuterProps {
  isOpen: boolean
  title: string
  initialValues: AmountOverridePayload
  options: ScopeOptions
  currencyOptions: FormSelectOption[]
  onClose(): void
  onSubmit(values: AmountOverridePayload): Promise<void>
}

const limitsValid = (limits: AmountOverridePayload['limits']): boolean =>
  limits.length > 0 && limits.every((limit) => !!limit.currencyId && Number(limit.maxAmount) > 0)

const AmountOverrideFormUI: React.FC<OuterProps & FormikProps<AmountOverridePayload>> = (props) => {
  const { values, setFieldValue, options, currencyOptions, isOpen, onClose } = props

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Form>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{props.title}</DialogTitle>
          </DialogHeader>

          <OverrideScopeFields values={values} setFieldValue={setFieldValue} options={options} />

          <FormFieldLayout label='Enabled'>
            <Toggle
              name='enabled'
              value={values.enabled}
              onChange={(value) => setFieldValue('enabled', value)}
            />
          </FormFieldLayout>

          <FieldArray name='limits'>
            {(arrayHelpers) => (
              <>
                {values.limits.map((limit, index) => (
                  <FormFieldLayout key={index} label={`Per-withdrawal limit ${index + 1}`}>
                    <div className='flex w-full items-start gap-2'>
                      <FormSelectField
                        name={`limits.${index}.currencyId`}
                        label=''
                        value={limit.currencyId}
                        options={currencyOptions}
                        showSearch
                        onChange={(value) =>
                          setFieldValue(`limits.${index}.currencyId`, String(value))
                        }
                      />
                      <FormField name={`limits.${index}.maxAmount`} label='' type='number' />
                      <Button
                        type='button'
                        variant='ghost'
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        ×
                      </Button>
                    </div>
                  </FormFieldLayout>
                ))}
                <AddButton
                  text='Add currency limit'
                  onClick={() => arrayHelpers.push({ currencyId: '', maxAmount: 0 })}
                />
              </>
            )}
          </FieldArray>

          {!hasAtLeastOneScope(values) && (
            <p className='text-sm text-red-500'>
              Select at least one scope (Entity, Country, PSP/Method, Client Type or Wallet Type).
            </p>
          )}
          {!limitsValid(values.limits) && (
            <p className='text-sm text-red-500'>
              Add at least one currency limit with an amount greater than 0.
            </p>
          )}

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='button' disabled={!props.isValid} onClick={() => props.handleSubmit()}>
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Form>
    </Dialog>
  )
}

export const AmountOverrideForm = withFormik<OuterProps, AmountOverridePayload>({
  enableReinitialize: true,
  validateOnMount: true,
  validationSchema: amountOverrideValidationSchema,
  mapPropsToValues: (props) => props.initialValues,
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit({
        ...toScopePayload(values),
        enabled: values.enabled,
        limits: values.limits.map((limit) => ({
          currencyId: limit.currencyId,
          maxAmount: Number(limit.maxAmount),
        })),
      })
    } finally {
      setSubmitting(false)
    }
  },
})(AmountOverrideFormUI)
